import { datadogLogs } from '@datadog/browser-logs';
import { ApolloError } from '@apollo/client';
import { snapshot } from 'valtio';
import { Nullable } from 'common-types';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import {
  Connector,
  ConnectorFieldValue,
  ConnectorsByFamilyObject,
  ConnectorsIDPErrors,
  EConnectorFields,
  GetConnectorInput,
  GetConnectorResourcesInput,
  MonitorConnectorResourcesInput,
} from '../connectors-types';
import connectorService from '../services';
import {
  setConnectors,
  setConnectorsByFamily,
  setFilteredConnectorsByFamily,
  setIsLoadingConnectors,
  setSearchValue,
  updateSingleConnectorsField,
} from '../store-actions/connectors-store-actions';
import ConnectorsStore from '../stores/connectors-store';
import {
  createConnectorStatus,
  findAliasConnectorMatch,
  resolveConnectorIcon,
} from '../utils/connectors-utils';
import { isTokenExpired } from '../utils/credentials-utils';
import {
  openConnectorConfigureModal,
  openRepoSelectionModal,
  updateSelectedConnectorField,
} from './connector-configure-actions';
import { cloneDeep } from 'lodash';
import { OxCategoriesKeys } from '@oxappsec/ox-consolidated-categories';

export const loadConnectors = async (
  disableCache?: boolean,
): Promise<Connector[]> => {
  const result: Connector[] = await connectorService.getConnectors.execute(
    disableCache,
  );
  setConnectors(result);
  setIsLoadingConnectors(false);
  return result;
};

export const loadConnector = async (
  getConnectorInput: GetConnectorInput,
  disableCache?: boolean,
): Promise<Nullable<Connector>> => {
  const result = await connectorService.getConnector.execute(
    getConnectorInput,
    disableCache,
  );
  if (!result) {
    return null;
  }
  return result.getConnector;
};

export const loadConnectorsByFamily = async (
  disableCache?: boolean,
): Promise<ConnectorsByFamilyObject[]> => {
  const result: ConnectorsByFamilyObject[] =
    await connectorService.getConnectorsByFamily.execute(disableCache);
  const deepClone = cloneDeep(result);
  const connectorsArray: Connector[] = [];
  deepClone.forEach(connectorsByFamilyObj => {
    connectorsArray.push(...connectorsByFamilyObj.connectors);
  });

  enrichConnectors(connectorsArray, deepClone);
  setConnectors(connectorsArray);
  setConnectorsByFamily(deepClone);
  setFilteredConnectorsByFamily(deepClone);
  setIsLoadingConnectors(false);
  return deepClone;
};

const enrichConnectors = (
  connectors: Connector[],
  allConnectorsByFamily: ConnectorsByFamilyObject[],
) => {
  connectors.forEach(connector => {
    connector.isConnectorInvalid = isTokenExpired(
      connector.credentials[0]?.tokenExpirationDate,
    );
    const connectorToUse = connector.aliasFor
      ? findAliasConnectorMatch(connector, allConnectorsByFamily)
      : undefined;
    connector.connectorStatus = createConnectorStatus(
      connectorToUse || connector,
    );

    connector.connectorToUse = connectorToUse;
    connector.badgeIcon = resolveConnectorIcon(connector.displayName);
  });
};

export const loadConnectorResources = async (
  getConnectorResourcesInput: GetConnectorResourcesInput,
  disableCache?: boolean,
) => {
  try {
    const result = await connectorService.getConnectorResources.execute(
      getConnectorResourcesInput,
      disableCache,
    );
    return result.getConnectorResources.resources;
  } catch (err) {
    let errMsg =
      'Error trying to fetch repositories, please check your credentials and try again later';
    if (err instanceof ApolloError) {
      errMsg = err.graphQLErrors?.[0].message || errMsg;
    }
    datadogLogs.logger.error(`loadConnectorResources failed: ${err}`);
    openSnackbar(errMsg, { variant: 'error', autoHideDuration: 15000 });
    return null;
  }
};

export const monitorConnectorResources = async (
  monitorConnectorResourceInput: MonitorConnectorResourcesInput,
) => {
  const result = await connectorService.monitorConnectorResources.execute(
    monitorConnectorResourceInput,
  );
  if (!result) {
    return null;
  }
  return result.monitorConnectorResources.success;
};

export const handleSearchConnector = (searchInput: string) => {
  setSearchValue(searchInput);
  const { connectorsByFamily } = snapshot(ConnectorsStore);
  const newFilteredConnectorsByFamily = connectorsByFamily.reduce(
    (acc: ConnectorsByFamilyObject[], familyObject) => {
      if (
        familyObject.familyDisplayName
          .toLowerCase()
          .includes(searchInput.toLowerCase())
      ) {
        acc.push(familyObject);
        return acc;
      }
      const filteredFamilyConnectors = familyObject.connectors.filter(
        connector =>
          connector.name.toLowerCase().includes(searchInput.toLowerCase()),
      );
      if (filteredFamilyConnectors.length === 0) {
        return acc;
      }
      acc.push({ ...familyObject, connectors: filteredFamilyConnectors });
      return acc;
    },
    [],
  );
  setFilteredConnectorsByFamily(newFilteredConnectorsByFamily);
};

export const findConnectorById = (connectorId: string) => {
  const { connectors } = snapshot(ConnectorsStore);
  return connectors.find(connector => connector.id === connectorId);
};

export const handleConnectorsPageQuery = (
  connectorId: string | null,
  isIdp: string | null,
  idpError: string | null,
) => {
  if (idpError) {
    openSnackbar(createIdpErrorMessage(idpError as ConnectorsIDPErrors), {
      variant: 'error',
      key: idpError,
      autoHideDuration: 15000,
    });
  }

  if (connectorId) {
    const connector = findConnectorById(connectorId);
    if (!connector) {
      return;
    }
    openConnectorConfigureModal(
      connector.aliasFor ? findAliasConnectorMatch(connector).id : connector.id,
      connector.id,
    );

    if (
      connector.family === OxCategoriesKeys.SourceControl &&
      isIdp &&
      !idpError
    ) {
      openRepoSelectionModal(true);
    }
  }
};

const createIdpErrorMessage = (idpError: ConnectorsIDPErrors) => {
  switch (idpError) {
    case ConnectorsIDPErrors.AccessDenied:
      return 'Identity Provider access was denied by the user';
    case ConnectorsIDPErrors.ValidationError:
      return 'There was a problem validating your credentials, there might be a problem with your access policy';
    case ConnectorsIDPErrors.GeneralError:
    case ConnectorsIDPErrors.MissingCode:
    case ConnectorsIDPErrors.MissingState:
      return 'Oops! We encountered a problem connecting, please try again later';
    default:
      return idpError;
  }
};

export const resetConnectorsStore = () => {
  const { connectorsByFamily } = snapshot(ConnectorsStore);
  setFilteredConnectorsByFamily(connectorsByFamily);
  setSearchValue('');
};

export const updateConnectorField = (
  connectorId: string,
  fieldName: EConnectorFields,
  fieldValue: ConnectorFieldValue,
) => {
  updateSingleConnectorsField(connectorId, fieldName, fieldValue);
  updateSelectedConnectorField(connectorId, fieldName, fieldValue);
};

export const updateConnectorFields = (
  connectorId: string,
  keyToValueMap: Partial<Record<EConnectorFields, ConnectorFieldValue>>,
) => {
  Object.keys(keyToValueMap).forEach(key => {
    updateConnectorField(
      connectorId,
      key as EConnectorFields,
      keyToValueMap[key],
    );
  });
};
